import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface WorkShift {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
  hours_per_day: string;
  break_minutes: number | null;
}

interface WorkShiftForm {
  name: string;
  start_time: string;
  end_time: string;
  hours_per_day: string;
  break_minutes: string;
}

const emptyForm: WorkShiftForm = { name: '', start_time: '', end_time: '', hours_per_day: '', break_minutes: '' };

// start_time/end_time come back from the backend as "HH:MM:SS" (a plain
// TIME column) — trim to "HH:MM" for the <input type="time"> value.
const toInputTime = (value: string) => value.slice(0, 5);

export function WorkShifts() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<WorkShift[] | null>(null);
  const [editing, setEditing] = useState<'new' | WorkShift | null>(null);
  const [form, setForm] = useState<WorkShiftForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<WorkShift[]>('/work-shifts').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: WorkShift) => {
    setForm({
      name: item.name,
      start_time: toInputTime(item.start_time),
      end_time: toInputTime(item.end_time),
      hours_per_day: item.hours_per_day,
      break_minutes: item.break_minutes === null ? '' : String(item.break_minutes),
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof WorkShiftForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/work-shifts', form);
      else if (editing) await api.put(`/work-shifts/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: WorkShift) => {
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/work-shifts/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Work Shifts</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>From</th>
                <th>To</th>
                <th>Hours Per Day</th>
                <th>Break</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="muted">{toInputTime(item.start_time)}</td>
                  <td className="muted">{toInputTime(item.end_time)}</td>
                  <td className="muted">{item.hours_per_day}</td>
                  <td className="muted">{item.break_minutes === null ? '—' : `${item.break_minutes} min`}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted center">
                    No work shifts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add work shift' : 'Edit work shift'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              From *
              <input type="time" value={form.start_time} onChange={set('start_time')} required />
              {errors.start_time && <small className="error">{errors.start_time[0]}</small>}
            </label>
            <label>
              To *
              <input type="time" value={form.end_time} onChange={set('end_time')} required />
              {errors.end_time && <small className="error">{errors.end_time[0]}</small>}
            </label>
            <label>
              Hours Per Day *
              <input type="number" step="0.25" min="0" max="24" value={form.hours_per_day} onChange={set('hours_per_day')} required />
              {errors.hours_per_day && <small className="error">{errors.hours_per_day[0]}</small>}
            </label>
            <label>
              Break (minutes)
              <input type="number" step="1" min="0" max="1440" value={form.break_minutes} onChange={set('break_minutes')} />
              {errors.break_minutes && <small className="error">{errors.break_minutes[0]}</small>}
              <small className="muted">Doesn't change Hours Per Day — just records the break already factored into it.</small>
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
